// Copyright (c) 2019-2023 Cigent Technology Inc. All Rights Reserved.
import {Injectable} from '@angular/core';
import { HttpClient, HttpErrorResponse, HttpHeaders } from '@angular/common/http';
import {TranslateService} from '@ngx-translate/core';
import {Observable, throwError} from 'rxjs';
import {catchError, map} from 'rxjs/operators';
import {API_URL} from 'src/shared/api';
import {IKeysResponse} from '../types/key';
import {IEncryptionHeaderResponse} from '../types/encryption-header';
import {ConfigurationService} from './configuration-service';
import {ISessionResponse} from '../app/dialogs/header-info/header-info.component';
import {IGroupSettings} from '../app/pages/groups/group-settings';

const CIGENT_PASSWORD_HEADER = 'X-Cigent-Key-Password';

export enum EncryptionKeyImportStatus {
  SUCCESS = "success",
  KEY_TOO_SHORT = "key-size-too-small",
  INVALID_FORMAT = "invalid-format",
  UNSUPPORTED_ALGO = "unsupported-algorithm",
  DECRYPT_FAILED = "decrypt-failed",
}

export interface IExportD3eResponse {
  success: boolean;
  data: string;
}

export interface IPostKeyResponse {
  status: EncryptionKeyImportStatus;
  newId: number;
}

export interface IKeyDownloadResponse {
  key: string;
}

@Injectable({
  providedIn: 'root'
})
export class EncryptionService {

  constructor(
    private http: HttpClient,
    private translate: TranslateService,
    private configurationService: ConfigurationService,
  ) { }

  getUrl(suffix: string) {
      return `${this.configurationService.apiUrl}${suffix}`;
  }

  decryptHeader(encodedHeader: string, password: string = null, filename: string = null): Observable<IEncryptionHeaderResponse> {
    const url = this.getUrl(API_URL.ENCRYPTION_HEADER_DECRYPT);
    const json_body = {
      'header': encodedHeader,
      'password': password,
      'filename': filename,
    };
    return this.http.post<IEncryptionHeaderResponse>(url, json_body);
  }

  getSessionInfo(encodedHeader: string): Observable<ISessionResponse> {
    const url = this.getUrl(API_URL.ENCRYPTION_HEADER_SESSIONS);
    const json_body = {
      'header': encodedHeader
    };
    return this.http.post<ISessionResponse>(url, json_body);
  }

  deleteAllEncryptionKeys(): Observable<string> {
    const url = this.getUrl(API_URL.ENCRYPTION_DELETE_ALL_KEYS);
    return this.http.delete(url).pipe(catchError(this.handleError.bind(this)));
  }

  getKeys(): Observable<IKeysResponse> {
    const url = this.getUrl(API_URL.ENCRYPTION_KEYS);
    return this.http.get<IKeysResponse>(url).pipe(catchError(this.handleError.bind(this)));
  }

  putKey(keyId: number, name: string, expires: Date, selectedOrgId = ''): Observable<{success: boolean}> {
    let url = this.getUrl(`${API_URL.ENCRYPTION_KEY}/${keyId}`);
    if (selectedOrgId) {
      url = `${url}?selectedOrgId=${selectedOrgId}`;
    }
    const request = {name, expires: !!expires ? expires.getTime() / 1000 : null};
    return this.http.put<{success: boolean}>(url, request);
  }

  importKeyFile(name: string, password: string, file: File, expirationDate: string): Observable<IPostKeyResponse> {
    const url = this.getUrl(API_URL.ENCRYPTION_KEYS);
    const formData = new FormData();
    formData.append('name', name);
    formData.append('password', password);
    formData.append('file', file, file.name);
    if (expirationDate) {
      formData.append('expirationDate', expirationDate);
    }
    return this.http.post<IPostKeyResponse>(url, formData);
  }

  getPublicKey(keyId: number): Observable<string> {
    const url = this.getUrl(`/api/v1/key/${keyId}/public`);
    return this.http.get<IKeyDownloadResponse>(url).pipe(map(result => result.key));
  }

  getPrivateKey(keyId: number, password: string): Observable<string> {
    const url = this.getUrl(`/api/v1/key/${keyId}/private`);
    const headers = new HttpHeaders().set(CIGENT_PASSWORD_HEADER, password);
    return this.http.get<IKeyDownloadResponse>(url, {headers}).pipe(map(result => result.key));
  }

  getProtectedKey(keyId: number): Observable<string> {
    const url = this.getUrl(`/api/v1/key/${keyId}/protected`);
    return this.http.get<IKeyDownloadResponse>(url).pipe(map(result => result.key));
  }

  getSharedKey(keyId: number, selectedOrgId = ''): Observable<IGroupSettings> {
    const url = this.getUrl(`/api/v1/shared-key/${keyId}?selectedOrgId=${selectedOrgId}`);
    return this.http.get<IGroupSettings>(url);
  }

  putSharedKey(keyId: number, groupSettings: IGroupSettings, selectedOrgId = ''): Observable<IGroupSettings> {
    let url = this.getUrl(`/api/v1/shared-key/${keyId}`);
    if (selectedOrgId) {
      url = url + `?selectedOrgId=${selectedOrgId}`;
    }
    return this.http.put<IGroupSettings>(url, groupSettings);
  }

  postSharedKey(groupSettings: IGroupSettings, selectedOrgId=''): Observable<{ new_id: number }> {
    let url = this.getUrl('/api/v1/shared-key');
    if (selectedOrgId) {
      url = url + `?selectedOrgId=${selectedOrgId}`;
    }
    return this.http.post<{new_id: number}>(url, groupSettings);
  }

  getD3eKeys(password: string): Observable<any> {
    const url = this.getUrl(`/api/v1/keys/export`);
    const headers = new HttpHeaders().set(CIGENT_PASSWORD_HEADER, password);
    const options = {
      headers: headers,
      responseType: 'blob' as 'json'
    };
    return this.http.get(url, options);
  }

  handleError(errResponse: HttpErrorResponse | any): any {
    if ((errResponse instanceof HttpErrorResponse) && errResponse?.error?.status) {
      return new Promise((resolve, reject) => {
        this.translate.get(errResponse.error.status).subscribe((translated) => reject(translated),
                                                           (err) => reject('Server Error'));
      });
    }
    return throwError(errResponse.error || 'Server error');
  }

}
